import type { ItineraryItem } from '@/types/itinerary';
import type {
  EnRouteDirection,
  Trip,
  TripCollaborator,
  TripPlace,
  TripPlaceType,
} from '@/types/trip';

// Shape of the server's materialized trip (the `hydrate` payload / GET /state).
// Declared structurally on the client so we don't import server types.
export interface HydratedPlace {
  placeId: string;
  item: ItineraryItem;
  placeType: string | null;
  scheduledDay: number | null;
  scheduledDayEnd: number | null;
  scheduledTime: string | null;
  estimatedDurationMinutes: number | null;
  checkInTime: string | null;
  checkInEndTime: string | null;
  checkOutTime: string | null;
  /** RETIRED 2026-08-02 — see `Trip.checkOutEndTime`. Still mapped so stored
   *  values round-trip. */
  checkOutEndTime: string | null;
  note: string | null;
  immichPhotos: unknown;
}

export interface HydratedTrip {
  id: string;
  name: string | null;
  location: string | null;
  lat: number | null;
  lng: number | null;
  radius: number | null;
  startDate: string | null;
  endDate: string | null;
  immichAlbumId: string | null;
  immichPhotos: unknown;
  places: Array<HydratedPlace>;
  collaborators: Array<{
    collaboratorId: string;
    email: string | null;
    role: string | null;
    immichUserId: string | null;
  }>;
  enRoute: Array<{ noteId: string; text: string | null; direction: string | null }>;
}

const today = (): string => new Date().toISOString().slice(0, 10);

const toPlace = (p: HydratedPlace): TripPlace => ({
  addedAt: 0,
  item: p.item,
  placeType: (p.placeType ?? 'activity') as TripPlaceType,
  scheduledDay: p.scheduledDay,
  scheduledDayEnd: p.scheduledDayEnd,
  scheduledTime: p.scheduledTime,
  estimatedDurationMinutes: p.estimatedDurationMinutes,
  checkInTime: p.checkInTime,
  checkInEndTime: p.checkInEndTime,
  checkOutTime: p.checkOutTime,
  checkOutEndTime: p.checkOutEndTime,
  note: p.note,
  immichPhotos: (p.immichPhotos ?? []) as TripPlace['immichPhotos'],
});

/** Map the server's materialized trip into the client `Trip` shape used for
 *  rendering. Called on WebSocket hydrate and initial load. */
export const tripStateToTrip = (h: HydratedTrip): Trip => ({
  id: h.id,
  name: h.name ?? 'My Trip',
  location: h.location ?? '',
  lat: h.lat,
  lng: h.lng,
  radius: h.radius,
  startDate: h.startDate ?? today(),
  endDate: h.endDate ?? today(),
  createdAt: 0,
  immichAlbumId: h.immichAlbumId ?? undefined,
  immichPhotos: (h.immichPhotos ?? []) as Trip['immichPhotos'],
  places: h.places.map(toPlace),
  collaborators: h.collaborators.map(
    (c): TripCollaborator => ({
      email: c.email ?? '',
      ...(c.immichUserId ? { immichUserId: c.immichUserId } : {}),
      addedAt: 0,
    }),
  ),
  enRoute: h.enRoute.map((n) => ({
    id: n.noteId,
    text: n.text ?? '',
    ...(n.direction ? { direction: n.direction as EnRouteDirection } : {}),
  })),
  deletedPlaceIds: {},
});
